import React, { useContext } from 'react';
import { MDText } from 'i18n-react';

import { Button } from '@motorway/mw-highway-code';

import cypressIds from 'Components/cypress_ids.json';
import { useVehicleData } from 'Context/VehicleContext/VehicleDataContext';
import { VEHICLE_PURCHASE_MODAL_TYPES, VehicleInfoContext } from 'Context/VehicleInfoContext';
import {
	triggerBuyItNowButtonClickedEventRedesign,
	triggerIncreaseOfferButtonClickedEvent,
	triggerMakeAnOfferButtonClickedEvent,
} from 'Services/analytics/events';
import { applyCypressData } from 'Utilities/helpers';

import { BiddingSectionCTAStateController } from '../BiddingSectionCTAStateController/BiddingSectionCTAStateController';
import { PurchaseModal } from '../PurchaseModal/PurchaseModal';
import { useBiddingSection } from '../useBiddingSection';

import buyItNowTexts from './BuyItNow.texts';

import styles from './BuyItNow.module.scss';

const LocalT = new MDText(buyItNowTexts);

const MakeOfferButton: React.FC = () => {
	const { showVehiclePurchaseModal } = useContext(VehicleInfoContext);

	const handleMakeOfferClick = () => {
		triggerMakeAnOfferButtonClickedEvent();

		showVehiclePurchaseModal({ modalType: VEHICLE_PURCHASE_MODAL_TYPES.MAKE_OFFER });
	};

	return (
		<Button
			data-thc-button
			fullWidth={true}
			{...applyCypressData(cypressIds.buttons.makeOffer)}
			label={LocalT.translate('makeAnOfferButton')}
			onClick={handleMakeOfferClick}
			variant="secondary"
		/>
	);
};

const IncreaseOfferButton: React.FC = () => {
	const { showVehiclePurchaseModal } = useContext(VehicleInfoContext);

	const handleIncreaseOfferClick = () => {
		triggerIncreaseOfferButtonClickedEvent();

		showVehiclePurchaseModal({ modalType: VEHICLE_PURCHASE_MODAL_TYPES.INCREASE_OFFER });
	};

	return (
		<Button
			data-thc-button
			fullWidth={true}
			label={LocalT.translate('increaseOfferButton')}
			{...applyCypressData(cypressIds.buttons.increaseOffer)}
			onClick={handleIncreaseOfferClick}
			variant="secondary"
		/>
	);
};

const BuyItNowButton: React.FC = () => {
	const { showVehiclePurchaseModal } = useContext(VehicleInfoContext);

	const handleBuyItNowClick = () => {
		triggerBuyItNowButtonClickedEventRedesign();

		showVehiclePurchaseModal({ modalType: VEHICLE_PURCHASE_MODAL_TYPES.BUY_IT_NOW });
	};

	return (
		<Button
			data-thc-button
			{...applyCypressData(cypressIds.buttons.buyItNow)}
			fullWidth={true}
			label={LocalT.translate('buyItNowButton')}
			onClick={handleBuyItNowClick}
			variant="primary"
		/>
	);
};

export const BuyItNowActions: React.FC = () => {
	const { latestUserBidOrOffer } = useBiddingSection();

	const hasUserBidOrOffered = latestUserBidOrOffer !== null;

	return (
		<div className={styles.buttonContainer}>
			<BuyItNowButton />
			<div className={styles.seperator}>{LocalT.translate('CTASeperator')}</div>
			{hasUserBidOrOffered ? <IncreaseOfferButton /> : <MakeOfferButton />}
		</div>
	);
};

export const BuyItNowCTA: React.FC = () => {
	const { bidding, state } = useVehicleData();
	const { hasOtherContactActiveBid, latestUserBidOrOffer } = bidding;

	return (
		<BiddingSectionCTAStateController
			hasOtherContactActiveBid={hasOtherContactActiveBid}
			latestUserBidOrOffer={latestUserBidOrOffer}
			state={state}
		>
			<BuyItNowActions />
			<PurchaseModal />
		</BiddingSectionCTAStateController>
	);
};
